import { useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { setQuery, useLocation } from '../../lib/router';
import { AsyncButton, Badge, Card, Empty, ErrorBox, Field, Input, Loading, PageHeader, Select, Tabs, Textarea, cx, useToast } from '../../ui/components';

export const kg = (grams: number | null | undefined): string => (grams ? `${(Number(grams) / 1000).toFixed(Number(grams) % 1000 ? 1 : 0)} kg` : '—');

/** The member's own programme, logging a workout and progress per exercise. */
export function TrainingPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'program';
  const program = useApi(() => api(`${T(gym.slug)}/me/program`), []);
  const history = useApi(() => api<any[]>(`${T(gym.slug)}/me/workouts`), []);
  const progress = useApi(() => api<any[]>(`${T(gym.slug)}/me/progress`), []);
  const goal = useApi(() => api<any>(`${T(gym.slug)}/me/goal`), []);
  const [entry, setEntry] = useState<Record<string, { reps: string; weight: string }>>({});
  const [feeling, setFeeling] = useState('good');
  const [notes, setNotes] = useState('');

  const save = async (): Promise<void> => {
    const sets = Object.entries(entry).filter(([, v]) => v.reps || v.weight).map(([exerciseId, v], i) => ({
      exerciseId, setNo: i + 1, reps: Number(v.reps) || null, weightGrams: v.weight ? Math.round(Number(v.weight) * 1000) : null,
    }));
    const r = await api(`${T(gym.slug)}/me/workouts`, { body: { programId: program.data?.id ?? null, feeling, notes, sets } });
    setEntry({});
    setNotes('');
    toast.ok(r.records?.length ? i18n.t('training.record', { exercise: r.records[0].exercise, weight: kg(r.records[0].weightGrams) }) : i18n.t('training.saved'));
    history.reload();
    progress.reload();
    setQuery({ tab: 'history' });
  };

  if (program.error) return <div className="page"><ErrorBox error={program.error} onRetry={program.reload} /></div>;
  return (
    <div className="page">
      <PageHeader title={i18n.t('training.title')} subtitle={program.data?.name} />
      {goal.data && (
        <Card title={i18n.t('goal.title')}>
          <div className="row between wrap">
            <div>
              {goal.data.target > 0 ? (
                <>
                  <strong>{i18n.t('goal.progress', { done: goal.data.doneThisWeek, target: goal.data.target })}</strong>
                  <div className="small muted">
                    {goal.data.streakWeeks > 0 ? i18n.t('goal.streak', { weeks: goal.data.streakWeeks }) : i18n.t('goal.no_streak')}
                    {goal.data.longestStreakWeeks > goal.data.streakWeeks ? ` · ${i18n.t('goal.longest', { weeks: goal.data.longestStreakWeeks })}` : ''}
                  </div>
                </>
              ) : <span className="muted">{i18n.t('goal.none')}</span>}
            </div>
            <div className="row gap-sm">
              <Select aria-label={i18n.t('goal.set')} value={String(goal.data.target || '')} placeholder={i18n.t('goal.set')}
                onChange={async (e) => { await api(`${T(gym.slug)}/me/goal`, { method: 'PUT', body: { target: e.target.value ? Number(e.target.value) : null } }); goal.reload(); }}
                options={[1, 2, 3, 4, 5, 6].map((n) => ({ value: String(n), label: i18n.t('goal.times_week', { count: n }) }))} />
            </div>
          </div>
          <div className="week-strip">
            {goal.data.weeks.map((w: any) => (
              <span key={w.week} className={cx('week-dot', w.met && 'met')} title={`${w.week}: ${w.sessions}`} />
            ))}
          </div>
        </Card>
      )}
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={[
        { id: 'program', label: i18n.t('training.program') },
        { id: 'history', label: i18n.t('training.history') },
        { id: 'progress', label: i18n.t('training.progress') },
      ]} />

      {tab === 'program' && (!program.data ? (program.loading ? <Loading /> : <Empty icon="🏋">{i18n.t('training.no_program')}</Empty>) : (
        <>
          {program.data.description && <p className="muted">{program.data.description}</p>}
          {(program.data.items ?? []).map((item: any) => (
            <Card key={item.id}>
              <div className="row between wrap">
                <div>
                  <strong>{item.exercise}</strong>
                  <div className="small muted">
                    {[`${item.sets ?? 3} × ${item.reps ?? '—'}`, item.weight_grams ? kg(item.weight_grams) : null, item.equipment,
                      item.rest_seconds ? `${item.rest_seconds}s ${i18n.t('training.rest')}` : null].filter(Boolean).join(' · ')}
                  </div>
                  {item.notes && <div className="small">{item.notes}</div>}
                </div>
                <div className="row gap-sm">
                  <Field label={i18n.t('training.reps')}>
                    <Input className="input input-sm" type="number" min={0} value={entry[item.exercise_id]?.reps ?? ''}
                      onChange={(e) => setEntry({ ...entry, [item.exercise_id]: { reps: e.target.value, weight: entry[item.exercise_id]?.weight ?? '' } })} />
                  </Field>
                  <Field label={i18n.t('training.weight')}>
                    <Input className="input input-sm" type="number" min={0} step="0.5" value={entry[item.exercise_id]?.weight ?? ''}
                      onChange={(e) => setEntry({ ...entry, [item.exercise_id]: { reps: entry[item.exercise_id]?.reps ?? '', weight: e.target.value } })} />
                  </Field>
                </div>
              </div>
            </Card>
          ))}
          <Card>
            <div className="grid-2">
              <Field label={i18n.t('training.feeling')}>
                <Select value={feeling} onChange={(e) => setFeeling(e.target.value)} options={['easy', 'good', 'hard'].map((f) => ({ value: f, label: i18n.option(f) }))} />
              </Field>
              <Field label={i18n.field('notes')}><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
            </div>
            <AsyncButton disabled={!Object.keys(entry).length} onClick={save}>{i18n.t('training.save_workout')}</AsyncButton>
          </Card>
        </>
      ))}

      {tab === 'history' && (!history.data ? <Loading /> : history.data.length === 0 ? <Empty icon="🕑">{i18n.t('training.no_workouts')}</Empty> : history.data.map((w) => (
        <Card key={w.id} title={i18n.dateTime(w.performed_at)} actions={w.feeling ? <Badge value={w.feeling} /> : null}>
          <ul className="sets">
            {(w.sets ?? []).map((s: any) => <li key={s.id}>{s.exercise}: {s.reps ?? '—'} × {kg(s.weight_grams)}</li>)}
          </ul>
          {w.notes && <p className="small muted">{w.notes}</p>}
        </Card>
      )))}

      {tab === 'progress' && (!progress.data ? <Loading /> : progress.data.length === 0 ? <Empty icon="📈">{i18n.t('training.no_progress')}</Empty> : (
        <Card>
          <table className="table">
            <thead><tr><th>{i18n.t('training.exercise')}</th><th>{i18n.t('training.best')}</th><th>{i18n.t('training.sets')}</th><th>{i18n.t('training.last')}</th></tr></thead>
            <tbody>
              {progress.data.map((p) => (
                <tr key={p.exerciseId}>
                  <td data-label={i18n.t('training.exercise')}>{p.exercise}</td>
                  <td data-label={i18n.t('training.best')}><strong>{kg(p.bestGrams)}</strong>{p.bestReps ? ` × ${p.bestReps}` : ''}</td>
                  <td data-label={i18n.t('training.sets')}>{p.sets}</td>
                  <td data-label={i18n.t('training.last')}>{i18n.date(p.lastAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}
